/**
 * Playback aspect telemetry — classify viewport / frame / painted-media boxes and list
 * which signals are currently vertical (for rising-edge Sentry + diagnostic snapshots).
 *
 * Aspect buckets match playlist filtering (`aspectRatioFromViewportDimensions` / layout canvas).
 */

import { AspectRatio } from '../database/custom-types/aspect-ratio'
import type { TScreenRotation } from '../models/screen-rotation'
import { aspectRatioFromViewportDimensions } from './aspectRatioFromPlaylistFrameLayouts'
import {
  classifyPlayerScreenAspectRatio,
  effectiveViewportDimensionsForAspect,
  type TPlayerScreenAspectClass
} from './playerScreenAspectRatio'

export type TVerticalAspectSignal
  = | 'viewport'
    | 'on_air_frame_layout'
    | 'visible_media_rect'

export interface IPlaybackAspectTelemetrySample {
  collectedAtMs: number
  innerWidthPx: number
  innerHeightPx: number
  screenRotation: TScreenRotation
  effectiveWidthPx: number
  effectiveHeightPx: number
  viewportAspect: AspectRatio
  screenAspectClass: TPlayerScreenAspectClass
  onAirFrameId?: string
  onAirFrameLayoutWidthPx?: number
  onAirFrameLayoutHeightPx?: number
  onAirFrameLayoutAspect?: AspectRatio
  /** Largest visible media element bounding box (CSS px), when sampled. */
  visibleMediaWidthPx?: number
  visibleMediaHeightPx?: number
  visibleMediaAspect?: AspectRatio
  aspectRestrictActive: boolean
}

export interface IBuildPlaybackAspectTelemetryInput {
  nowMs: number
  innerWidthPx: number
  innerHeightPx: number
  screenRotation: TScreenRotation
  aspectRestrictActive: boolean
  onAirFrameId?: string | null
  onAirFrameLayoutWidthPx?: number | null
  onAirFrameLayoutHeightPx?: number | null
  visibleMediaWidthPx?: number | null
  visibleMediaHeightPx?: number | null
}

function positiveInt(n: number | null | undefined): number | undefined {
  if (n == null || !Number.isFinite(n)) {
    return undefined
  }
  const r = Math.round(n)
  return r > 0 ? r : undefined
}

/**
 * Build a telemetry sample. Omits optional on-air / media fields when dimensions are missing.
 */
export function buildPlaybackAspectTelemetrySample(
  input: IBuildPlaybackAspectTelemetryInput
): IPlaybackAspectTelemetrySample {
  const innerW = Math.max(0, Math.round(input.innerWidthPx))
  const innerH = Math.max(0, Math.round(input.innerHeightPx))
  const rotation = input.screenRotation
  const effective = effectiveViewportDimensionsForAspect(innerW, innerH, rotation)
  const sample: IPlaybackAspectTelemetrySample = {
    collectedAtMs: input.nowMs,
    innerWidthPx: innerW,
    innerHeightPx: innerH,
    screenRotation: rotation,
    effectiveWidthPx: effective.width,
    effectiveHeightPx: effective.height,
    viewportAspect: aspectRatioFromViewportDimensions(effective.width, effective.height),
    screenAspectClass: classifyPlayerScreenAspectRatio(innerW, innerH, rotation),
    aspectRestrictActive: input.aspectRestrictActive === true
  }

  const frameId = input.onAirFrameId
  if (frameId != null && frameId !== '') {
    sample.onAirFrameId = frameId
  }
  const flw = positiveInt(input.onAirFrameLayoutWidthPx)
  const flh = positiveInt(input.onAirFrameLayoutHeightPx)
  if (flw != null && flh != null) {
    sample.onAirFrameLayoutWidthPx = flw
    sample.onAirFrameLayoutHeightPx = flh
    sample.onAirFrameLayoutAspect = aspectRatioFromViewportDimensions(flw, flh)
  }

  const mw = positiveInt(input.visibleMediaWidthPx)
  const mh = positiveInt(input.visibleMediaHeightPx)
  if (mw != null && mh != null) {
    sample.visibleMediaWidthPx = mw
    sample.visibleMediaHeightPx = mh
    sample.visibleMediaAspect = aspectRatioFromViewportDimensions(mw, mh)
  }

  return sample
}

/** Landscape walls only. Portrait panels are supposed to be vertical. */
export function isLandscapeScreenAspectClass(
  screenAspectClass: TPlayerScreenAspectClass | null | undefined
): boolean {
  return typeof screenAspectClass === 'string' && screenAspectClass.startsWith('landscape_')
}

/**
 * Vertical signals that are unexpected: a landscape screen whose viewport, on-air
 * layout, or painted media went portrait. Empty on portrait and unknown classes.
 */
export function unexpectedVerticalAspectSignals(
  sample: Pick<
    IPlaybackAspectTelemetrySample,
    'viewportAspect' | 'onAirFrameLayoutAspect' | 'visibleMediaAspect' | 'screenAspectClass'
  >
): TVerticalAspectSignal[] {
  if (!isLandscapeScreenAspectClass(sample.screenAspectClass)) {
    return []
  }
  return verticalAspectSignals(sample)
}

/** Which sampled boxes currently classify as vertical (height > width). */
export function verticalAspectSignals(
  sample: Pick<
    IPlaybackAspectTelemetrySample,
    'viewportAspect' | 'onAirFrameLayoutAspect' | 'visibleMediaAspect'
  >
): TVerticalAspectSignal[] {
  const out: TVerticalAspectSignal[] = []
  if (sample.viewportAspect === AspectRatio.Vertical) {
    out.push('viewport')
  }
  if (sample.onAirFrameLayoutAspect === AspectRatio.Vertical) {
    out.push('on_air_frame_layout')
  }
  if (sample.visibleMediaAspect === AspectRatio.Vertical) {
    out.push('visible_media_rect')
  }
  return out
}

export function playbackAspectTelemetryHasVertical(
  sample: Pick<
    IPlaybackAspectTelemetrySample,
    'viewportAspect' | 'onAirFrameLayoutAspect' | 'visibleMediaAspect'
  >
): boolean {
  return verticalAspectSignals(sample).length > 0
}
